import {
  BOOSTER_EFFECT_DURATION_SEC,
  BOOSTER_SPEED_MULTIPLIER,
  type BoosterKind,
} from '../../domain/catalog/boosters';
import { getCourierMaxLives, getCourierRunStats } from '../../domain/catalog/courierStats';
import { VERTICAL_SPEED_BASE_FACTOR } from '../../domain/catalog/flightPhysics';
import type { CourierId } from '../../domain/ids/ids';
import type { RunnerLevel, RunnerPlayer, VerticalMoveDirection } from '../../domain/runner/runnerTypes';
import {
  hasRunnerHitInvulnerability,
  hasRunnerSpeedBooster,
} from '../../domain/runner/runnerPlayer';
import {
  RUNNER_FORWARD_SPEED_BASE_M_PER_SEC,
  RUNNER_HIT_INVULN_SEC,
  RUNNER_HIT_SPEED_MULTIPLIER,
  RUNNER_LANE_MAX,
  RUNNER_LANE_MIN,
  RUNNER_REF_VIEWBOX_WIDTH,
} from '../../domain/runner/runnerConstants';

export { BOOSTER_EFFECT_DURATION_SEC as RUNNER_BOOSTER_DURATION_SEC };

/** Скорость сдвига targetLane клавишами W/S (norm/sec), как в v1. */
export const KEYBOARD_TARGET_SPEED_NORM_PER_SEC = 1.35;

function forwardSpeedForLevel(level: RunnerLevel): number {
  return RUNNER_FORWARD_SPEED_BASE_M_PER_SEC * (level.viewBox.width / RUNNER_REF_VIEWBOX_WIDTH);
}

export function getRunnerForwardSpeed(level: RunnerLevel, player: RunnerPlayer): number {
  let speed =
    forwardSpeedForLevel(level) *
    (hasRunnerSpeedBooster(player) ? BOOSTER_SPEED_MULTIPLIER : 1);
  if (hasRunnerHitInvulnerability(player)) {
    speed *= RUNNER_HIT_SPEED_MULTIPLIER;
  }
  return speed;
}

export function moveRunnerForwardUseCase(
  level: RunnerLevel,
  player: RunnerPlayer,
  deltaSeconds: number,
  motionScale = 1,
): RunnerPlayer {
  if (motionScale <= 0) {
    return player;
  }
  const speed = getRunnerForwardSpeed(level, player) * motionScale;
  const nextWorldS = Math.min(player.worldS + speed * deltaSeconds, level.routeEndS);
  if (nextWorldS === player.worldS) {
    return player;
  }
  return { ...player, worldS: nextWorldS };
}

function clampLane(lane: number, laneMin: number, laneMax: number): number {
  return Math.min(laneMax, Math.max(laneMin, lane));
}

export function clampRunnerLane(level: RunnerLevel, lane: number): number {
  return clampLane(
    lane,
    level.laneMin ?? RUNNER_LANE_MIN,
    level.laneMax ?? RUNNER_LANE_MAX,
  );
}

export function moveRunnerVerticalUseCase(
  player: RunnerPlayer,
  courierId: CourierId,
  verticalDirection: VerticalMoveDirection,
  deltaSeconds: number,
  motionScale = 1,
  level?: Pick<RunnerLevel, 'laneMin' | 'laneMax'>,
): RunnerPlayer {
  if (motionScale <= 0 || deltaSeconds <= 0) {
    return player;
  }

  const laneMin = level?.laneMin ?? RUNNER_LANE_MIN;
  const laneMax = level?.laneMax ?? RUNNER_LANE_MAX;

  let targetLane = player.targetLane;
  if (verticalDirection !== 0) {
    targetLane = clampLane(
      targetLane +
        verticalDirection * KEYBOARD_TARGET_SPEED_NORM_PER_SEC * deltaSeconds,
      laneMin,
      laneMax,
    );
  } else {
    targetLane = clampLane(targetLane, laneMin, laneMax);
  }

  const handling = getCourierRunStats(courierId).handling;
  const maxSpeed = VERTICAL_SPEED_BASE_FACTOR * (handling / 6);
  const diff = targetLane - player.lane;
  const step = maxSpeed * deltaSeconds * motionScale;

  let nextLane = player.lane;
  if (Math.abs(diff) <= step) {
    nextLane = targetLane;
  } else {
    nextLane = player.lane + Math.sign(diff) * step;
  }
  nextLane = clampLane(nextLane, laneMin, laneMax);

  if (nextLane === player.lane && targetLane === player.targetLane) {
    return player;
  }
  return { ...player, lane: nextLane, targetLane };
}

export function tickRunnerBoosterUseCase(
  player: RunnerPlayer,
  deltaSeconds: number,
): RunnerPlayer {
  if (player.activeBooster == null) {
    return player;
  }
  const remainingSeconds = player.activeBooster.remainingSeconds - deltaSeconds;
  if (remainingSeconds <= 0) {
    return { ...player, activeBooster: null };
  }
  return {
    ...player,
    activeBooster: { ...player.activeBooster, remainingSeconds },
  };
}

export function tickRunnerHitInvulnerabilityUseCase(
  player: RunnerPlayer,
  deltaSeconds: number,
): RunnerPlayer {
  if (player.hitInvulnerableRemainingSeconds <= 0) {
    return player;
  }
  const remainingSeconds = player.hitInvulnerableRemainingSeconds - deltaSeconds;
  return {
    ...player,
    hitInvulnerableRemainingSeconds: Math.max(0, remainingSeconds),
  };
}

export function tickRunnerLevelUseCase(
  level: RunnerLevel,
  player: RunnerPlayer,
  courierId: CourierId,
  verticalDirection: VerticalMoveDirection,
  deltaSeconds: number,
  motionScale = 1,
): RunnerPlayer {
  if (motionScale <= 0) {
    return player;
  }
  const withBooster = tickRunnerBoosterUseCase(player, deltaSeconds);
  const withHitInvuln = tickRunnerHitInvulnerabilityUseCase(withBooster, deltaSeconds);
  const movedForward = moveRunnerForwardUseCase(
    level,
    withHitInvuln,
    deltaSeconds,
    motionScale,
  );
  return moveRunnerVerticalUseCase(
    movedForward,
    courierId,
    verticalDirection,
    deltaSeconds,
    motionScale,
    level,
  );
}

export function createSpawnRunnerPlayer(level: RunnerLevel, courierId: CourierId): RunnerPlayer {
  const maxLives = getCourierMaxLives(courierId);
  const startLane = clampRunnerLane(level, level.startLane);
  return {
    worldS: level.spawnSvgX - level.startPointSvgX,
    lane: startLane,
    targetLane: startLane,
    lives: maxLives,
    maxLives,
    activeBooster: null,
    hitInvulnerableRemainingSeconds: 0,
  };
}

export function startRunnerLevelUseCase(
  level: RunnerLevel,
  courierId: CourierId,
): { level: RunnerLevel; player: RunnerPlayer } {
  return {
    level,
    player: createSpawnRunnerPlayer(level, courierId),
  };
}

export function applyRunnerPlayerHitUseCase(player: RunnerPlayer, hitCount: number): RunnerPlayer {
  if (hitCount <= 0) {
    return player;
  }
  return {
    ...player,
    lives: Math.max(0, player.lives - hitCount),
    hitInvulnerableRemainingSeconds: RUNNER_HIT_INVULN_SEC,
  };
}

export function applyRunnerBoosterPickupUseCase(
  player: RunnerPlayer,
  kind: BoosterKind,
): RunnerPlayer {
  return {
    ...player,
    activeBooster: {
      kind,
      remainingSeconds: BOOSTER_EFFECT_DURATION_SEC,
    },
  };
}
